import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalProps } from "react-bootstrap";
import { ChevronDown, Minus, Plus } from "react-feather";
import { toast } from "react-toastify";
import Close from "../compoment/icon/Close";
import { AddCart } from "../compoment/icon";
import { formatPrice } from "../constants";
import {
    Pack,
    PackProduct,
    PackProductSelection,
    getPackDisplayImage,
    getPackDisplayPrice,
    getPackIncludedProducts,
    getPackProductKey,
    getPackUnitTotalFromSelections,
} from "../services/packService";
import PackOne from "../assets/img/new-theme/pack-1.png";
import PlaceholderImg from "../assets/img/e-commerce/placeholder-img.png";
import {
    asOptionArray,
    cleanOptionLabel,
    getProductColorLabel,
    getProductSizeLabel,
    normalizeMediaUrl,
    productNameIncludesSticker,
    resolveProductColorImage,
    resolveProductDisplayImage,
    uniqueProductColors,
    uniqueSortedProductSizes,
} from "../services/productService";
import ProductArtworkPreview from "../compoment/features/ProductArtworkPreview";
import PackShowcaseCollage from "../compoment/features/PackShowcaseCollage";
import AddtoCart from "./AddtoCart";

const PACK_COLORS = ["#07bfd3", "#f02b78", "#9c28e8", "#f2ab00"];

interface PackItemModalProps extends ModalProps {
    onHide: () => void;
    pack: Pack | null;
    packIndex?: number;
    initialQuantity?: number;
    favouriteImg?: string | null;
    onConfirm?: (quantity: number, selections: Record<string, PackProductSelection>) => void;
}

function getSizeLabel(size: any): string {
    return getProductSizeLabel(size);
}

function getColorLabel(color: any): string {
    return getProductColorLabel(color);
}

function isUsableImageUrl(value: unknown): value is string {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return lower !== "null" && lower !== "undefined" && lower !== "[object object]";
}

function getProductMediaHint(entry: PackProduct): string | undefined {
    return [
        entry.picture,
        entry.product?.picture,
        (entry.product as any)?.imgURL,
        (entry.product as any)?.image,
    ].find((value) => isUsableImageUrl(value) && /^https?:\/\//i.test(String(value).trim()));
}

function getColorImage(color: any, baseHint?: string | null): string | undefined {
    if (!color || typeof color !== "object") return undefined;
    const candidates = [
        color.productImageUrl,
        color.product_image_url,
        color.picture,
        color.image,
        color.imgURL,
        color.imageUrl,
        color.image_url,
    ];
    for (const candidate of candidates) {
        const normalized = normalizeMediaUrl(
            typeof candidate === "string" ? candidate : undefined,
            baseHint
        );
        if (normalized) return normalized;
    }
    return undefined;
}

function getProductSizes(entry: PackProduct): any[] {
    return uniqueSortedProductSizes(entry.product?.product_sizes);
}

function getProductColors(entry: PackProduct): any[] {
    const hint = getProductMediaHint(entry);
    return uniqueProductColors(
        entry.product?.color,
        (current, next) => !getColorImage(current, hint) && Boolean(getColorImage(next, hint))
    );
}

function getBaseProductImage(entry: PackProduct, fallback = PlaceholderImg): string {
    const hint = getProductMediaHint(entry);
    return (
        [
            resolveProductDisplayImage(entry.product),
            resolveProductDisplayImage(entry),
            normalizeMediaUrl(entry.picture, hint),
            normalizeMediaUrl(entry.product?.picture, hint),
            normalizeMediaUrl((entry.product as any)?.imgURL, hint),
            normalizeMediaUrl((entry.product as any)?.image, hint),
            fallback,
        ].find(isUsableImageUrl) || fallback
    );
}

function getProductThumb(entry: PackProduct, selection?: PackProductSelection, fallback = PlaceholderImg): string {
    const hint = getProductMediaHint(entry);
    const selectedColor = getProductColors(entry).find(
        (color) => getColorLabel(color).toLowerCase() === String(selection?.color || "").toLowerCase()
    );
    const colorImage =
        getColorImage(selectedColor, hint) ||
        resolveProductColorImage(entry.product, selection?.color);

    return (
        [
            colorImage,
            normalizeMediaUrl(selection?.picture, hint),
            getBaseProductImage(entry, fallback),
            fallback,
        ].find(isUsableImageUrl) || fallback
    );
}

function labelsMatch(a?: string | null, b?: string | null): boolean {
    const left = String(a || "").trim().toLowerCase();
    const right = String(b || "").trim().toLowerCase();
    return Boolean(left && right && left === right);
}

/** Admin-configured defaults from pack line item / units. */
function getAdminPackDefaults(entry: PackProduct): {
    size: string;
    color: string;
    inches?: string;
    unitPrice: number;
} {
    const unit =
        Array.isArray(entry.units) && entry.units.length > 0
            ? entry.units[0]
            : null;

    const size =
        getSizeLabel(entry.size) ||
        getSizeLabel(unit?.size) ||
        cleanOptionLabel(String(entry.size || unit?.size || "").split(",")[0] || "");
    const color =
        getColorLabel(entry.color) ||
        getColorLabel(unit?.color) ||
        cleanOptionLabel(typeof unit?.color === "string" ? unit.color : "") ||
        cleanOptionLabel(typeof entry.color === "string" ? entry.color : "");
    const inches = cleanOptionLabel(String(entry.inches || unit?.inches || "")) || undefined;
    const unitPrice =
        Number(entry.unit_price ?? unit?.unit_price ?? entry.price ?? 0) || 0;

    return { size, color, inches, unitPrice };
}

function findSizeForSelection(
    entry: PackProduct,
    sizeLabel?: string,
    colorLabel?: string
): any | null {
    const allSizes = asOptionArray(entry.product?.product_sizes);
    const uniqueSizes = getProductSizes(entry);
    const normalizedSize = String(sizeLabel || "").trim().toLowerCase();
    const normalizedColor = String(colorLabel || "").trim().toLowerCase();
    if (!normalizedSize) return uniqueSizes[0] || allSizes[0] || null;

    const sizeMatches = allSizes.filter(
        (size) => getSizeLabel(size).toLowerCase() === normalizedSize
    );
    if (sizeMatches.length === 0) {
        // Fallback: match against deduped option labels (handles nested size objects).
        return (
            uniqueSizes.find((size) => getSizeLabel(size).toLowerCase() === normalizedSize) ||
            null
        );
    }

    if (normalizedColor) {
        const withColor = sizeMatches.find((size) => {
            const sizeColor = getColorLabel(size.color || size.variety || size.colour);
            return sizeColor && sizeColor.toLowerCase() === normalizedColor;
        });
        if (withColor) return withColor;
    }

    return sizeMatches[0];
}

function resolveSelectionUnitPrice(
    entry: PackProduct,
    size: any | null,
    adminDefaults: ReturnType<typeof getAdminPackDefaults>
): number {
    const sizeLabel = getSizeLabel(size);
    // Keep admin pack price when the selected size is still the admin default.
    if (adminDefaults.size && labelsMatch(sizeLabel, adminDefaults.size) && adminDefaults.unitPrice > 0) {
        return adminDefaults.unitPrice;
    }

    return (
        Number(
            size?.price ??
            entry.unit_price ??
            entry.price ??
            entry.product?.price ??
            adminDefaults.unitPrice ??
            0
        ) || 0
    );
}

function buildDefaultSelections(products: PackProduct[]): Record<string, PackProductSelection> {
    const next: Record<string, PackProductSelection> = {};

    products.forEach((entry, index) => {
        const productKey = getPackProductKey(entry, index);
        const isSticker = productNameIncludesSticker(entry.name || entry.product?.name);
        const sizes = getProductSizes(entry);
        const colors = isSticker ? [] : getProductColors(entry);
        const adminDefaults = getAdminPackDefaults(entry);

        // Prefer admin-defined color; only fall back if admin did not set one.
        const matchedColor =
            (adminDefaults.color
                ? colors.find((color) => labelsMatch(getColorLabel(color), adminDefaults.color))
                : null) ||
            (!adminDefaults.color ? colors[0] : null) ||
            null;
        const colorLabel =
            (matchedColor ? getColorLabel(matchedColor) : "") ||
            adminDefaults.color ||
            undefined;

        // Prefer admin-defined size; only fall back to first size if admin did not set one.
        const matchedSize = adminDefaults.size
            ? findSizeForSelection(entry, adminDefaults.size, colorLabel)
            : sizes[0] || null;

        const mediaHint = getProductMediaHint(entry);
        const colorImage =
            getColorImage(matchedColor, mediaHint) ||
            resolveProductColorImage(entry.product, colorLabel) ||
            getBaseProductImage(entry);

        next[productKey] = {
            productKey,
            productId: String(entry.product_id || entry.product?.id || entry.id),
            productSizeId: matchedSize?.id,
            size: matchedSize ? getSizeLabel(matchedSize) : adminDefaults.size || undefined,
            inches: matchedSize?.inches || adminDefaults.inches || undefined,
            color: isSticker ? undefined : (colorLabel || undefined),
            picture: colorImage,
            sku_code: matchedSize?.sku_code || undefined,
            unit_price: resolveSelectionUnitPrice(entry, matchedSize, adminDefaults),
        };
    });

    return next;
}

const PackItemModal = ({
    show,
    onHide,
    pack,
    packIndex = 0,
    initialQuantity = 1,
    favouriteImg = null,
    onConfirm,
}: PackItemModalProps) => {
    const [count, setCount] = useState(Math.max(1, initialQuantity || 1));
    const [successModal, setSuccessModal] = useState(false);
    const [selections, setSelections] = useState<Record<string, PackProductSelection>>({});
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    const products = useMemo(() => getPackIncludedProducts(pack), [pack]);

    useEffect(() => {
        if (show && pack) {
            const included = getPackIncludedProducts(pack);
            setCount(Math.max(1, initialQuantity || 1));
            setSuccessModal(false);
            setSelections(buildDefaultSelections(included));
            setExpandedKey(included[0] ? getPackProductKey(included[0], 0) : null);
        }
    }, [show, initialQuantity, pack?.id]);

    if (!pack) return null;

    const packImage = getPackDisplayImage(pack, PackOne);
    const unitPrice =
        getPackUnitTotalFromSelections(pack, selections) || getPackDisplayPrice(pack);
    const totalPrice = unitPrice * count;
    const packColor = PACK_COLORS[packIndex % PACK_COLORS.length];
    const packName = typeof pack.name === "string" ? pack.name : "Pack";

    const increment = () => setCount((c) => c + 1);
    const decrement = () => setCount((c) => Math.max(1, c - 1));

    const updateSelection = (productKey: string, patch: Partial<PackProductSelection>) => {
        setSelections((prev) => ({
            ...prev,
            [productKey]: {
                ...prev[productKey],
                productKey,
                productId: prev[productKey]?.productId || "",
                ...patch,
            },
        }));
    };

    const validateSelections = () => {
        for (let index = 0; index < products.length; index += 1) {
            const entry = products[index];
            const productKey = getPackProductKey(entry, index);
            const selection = selections[productKey];
            const productName = entry.name || entry.product?.name || "Included product";
            const isSticker = productNameIncludesSticker(productName);
            const sizes = getProductSizes(entry);
            const colors = isSticker ? [] : getProductColors(entry);

            if (sizes.length > 0 && !selection?.productSizeId && !selection?.size) {
                toast.error(`Please select a size for ${productName}`);
                return false;
            }
            if (colors.length > 0 && !selection?.color) {
                toast.error(`Please select a color for ${productName}`);
                return false;
            }
        }
        return true;
    };

    const applySizeSelection = (entry: PackProduct, productKey: string, size: any) => {
        const sizeLabel = getSizeLabel(size);
        const currentColor = selections[productKey]?.color;
        const resolved = findSizeForSelection(entry, sizeLabel, currentColor) || size;
        const adminDefaults = getAdminPackDefaults(entry);

        updateSelection(productKey, {
            productSizeId: resolved?.id,
            size: sizeLabel,
            inches: resolved?.inches || size?.inches,
            sku_code: resolved?.sku_code || size?.sku_code || undefined,
            unit_price: resolveSelectionUnitPrice(entry, resolved || size, adminDefaults),
        });
    };

    const applyColorSelection = (entry: PackProduct, productKey: string, color: any) => {
        const colorLabel = getColorLabel(color);
        if (!colorLabel) return;

        const currentSize = selections[productKey]?.size;
        const resolved = findSizeForSelection(entry, currentSize, colorLabel);
        const adminDefaults = getAdminPackDefaults(entry);
        const mediaHint = getProductMediaHint(entry);
        const colorImage =
            getColorImage(color, mediaHint) ||
            resolveProductColorImage(entry.product, colorLabel) ||
            getBaseProductImage(entry);

        updateSelection(productKey, {
            color: colorLabel,
            picture: colorImage,
            ...(resolved
                ? {
                    productSizeId: resolved.id,
                    size: getSizeLabel(resolved) || currentSize,
                    inches: resolved.inches,
                    sku_code: resolved.sku_code || undefined,
                    unit_price: resolveSelectionUnitPrice(entry, resolved, adminDefaults),
                }
                : {}),
        });
    };

    const handleAddToCart = () => {
        if (count <= 0) return;
        if (!validateSelections()) return;

        onConfirm?.(count, selections);
        setSuccessModal(true);
        setTimeout(() => {
            setSuccessModal(false);
            onHide();
        }, 1600);
    };

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                centered
                scrollable
                className="packItemModal"
                backdrop="static"
            >
                <Modal.Body className="">
                    <button type="button" className="packItemClose" onClick={onHide} aria-label="Close">
                        <Close />
                    </button>

                    <div className="packItemLayout">
                        <div className="packItemLayoutLeft">
                            <div
                                className="packItemPreview packProductCard packShowcaseCard"
                                style={{ ["--pack-color" as any]: packColor }}
                            >
                                <div className="packShowcaseButton" role="presentation">
                                    <div className="packShowcaseTitle">{packName}</div>
                                    <div className="packShowcaseImageWrap">
                                        <PackShowcaseCollage
                                            pack={pack}
                                            products={products}
                                            favouriteImg={favouriteImg}
                                            fallbackImage={packImage}
                                        />
                                    </div>
                                    <ul className="packShowcaseFeatures">
                                        {products.map((product, index) => {
                                            const productName =
                                                product?.name || product?.product?.name || "Included product";
                                            const quantity = Number(product?.quantity || 1);

                                            return (
                                                <li key={product?.id || product?.product_id || `${productName}-${index}`}>
                                                    <span className="packShowcaseCheck" aria-hidden="true">✓</span>
                                                    <span className="packShowcaseFeatureName">
                                                        {quantity > 1 ? `${quantity} × ${productName}` : productName}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <div className="packShowcasePrice">{formatPrice(totalPrice)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="packItemLayoutRight">
                            <div className="packItemDetails headingLike">
                                <div className="packItemDetailsTitle">{packName}</div>
                                <div className="packItemDetailsSubtitle">Included products</div>

                                <div className="packItemAccordionList">
                                    {products.map((entry, index) => {
                                        const productKey = getPackProductKey(entry, index);
                                        const selection = selections[productKey];
                                        const productName = entry.name || entry.product?.name || "Included product";
                                        const quantity = Number(entry.quantity || 1);
                                        const isSticker = productNameIncludesSticker(productName);
                                        const sizes = getProductSizes(entry);
                                        const colors = isSticker ? [] : getProductColors(entry);
                                        const thumb = getProductThumb(entry, selection, PlaceholderImg);
                                        const productSku =
                                            selection?.sku_code ||
                                            (entry.product as any)?.sku_code ||
                                            (entry as any)?.sku_code;
                                        const productPlacement =
                                            (entry.product as any)?.artwork_placement ||
                                            (entry.product as any)?.artworkPlacement ||
                                            (entry.product as any)?.print_area ||
                                            (entry as any)?.artwork_placement ||
                                            (entry as any)?.print_area;
                                        const artSource =
                                            typeof favouriteImg === "string" ? favouriteImg.trim() : "";
                                        const isOpen = expandedKey === productKey;
                                        const sizeSummary = selection?.inches
                                            ? `${selection.size || ""} ${selection.inches}"`.trim()
                                            : selection?.size;
                                        const summaryBits = [
                                            sizeSummary,
                                            isSticker ? undefined : selection?.color,
                                        ].filter(Boolean);

                                        return (
                                            <div
                                                className={`packItemAccordion${isOpen ? " is-open" : ""}`}
                                                key={productKey}
                                            >
                                                <button
                                                    type="button"
                                                    className="packItemAccordionHeader"
                                                    onClick={() =>
                                                        setExpandedKey((prev) => (prev === productKey ? null : productKey))
                                                    }
                                                    aria-expanded={isOpen}
                                                >
                                                    {artSource ? (
                                                        <ProductArtworkPreview
                                                            key={`${productKey}-${selection?.color || "default"}-${thumb}-${artSource}`}
                                                            productImage={thumb || PlaceholderImg}
                                                            artworkImage={artSource}
                                                            itemKey={`pack-modal-${productKey}`}
                                                            productName={productName}
                                                            skuCode={productSku}
                                                            placement={productPlacement}
                                                            exportResult={false}
                                                            className="packItemDetailThumb"
                                                        />
                                                    ) : (
                                                        <img
                                                            key={`${productKey}-${selection?.color || "default"}-${thumb}`}
                                                            src={thumb || PlaceholderImg}
                                                            alt={productName}
                                                            className="packItemDetailThumb"
                                                            onError={(event) => {
                                                                const target = event.currentTarget;
                                                                if (target.dataset.fallbackApplied === "1") return;
                                                                target.dataset.fallbackApplied = "1";
                                                                target.src = PlaceholderImg;
                                                            }}
                                                        />
                                                    )}
                                                    <div className="packItemDetailMeta text-capitalize">
                                                        <div className="packItemDetailName">
                                                            {quantity > 1 ? `${quantity} × ${productName}` : productName}
                                                        </div>
                                                        {summaryBits.length > 0 && (
                                                            <div className="packItemDetailSummary">
                                                                {summaryBits.join(" · ")}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <ChevronDown
                                                        className={`packItemAccordionChevron${isOpen ? " is-open" : ""}`}
                                                        size={22}
                                                    />
                                                </button>

                                                {isOpen && (
                                                    <div className="packItemAccordionBody">
                                                        {sizes.length > 0 && (
                                                            <div className="packItemOptionBlock">
                                                                <div className="packItemOptionLabel">Size</div>
                                                                <div className="packItemOptionList packItemSizeContainer">
                                                                    {sizes.map((size, sizeIndex) => {
                                                                        const label = getSizeLabel(size);
                                                                        const isSelected =
                                                                            String(selection?.size || "").trim().toLowerCase() ===
                                                                            label.toLowerCase();
                                                                        return (
                                                                            <button
                                                                                type="button"
                                                                                key={`${productKey}-size-${label}-${sizeIndex}`}
                                                                                className={`packItemSingleSize text-capitalize ${isSelected ? " is-selected" : ""}`}
                                                                                onClick={() => applySizeSelection(entry, productKey, size)}
                                                                            >
                                                                                {label}
                                                                                {size?.inches ? ` ${size.inches}"` : ""}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {colors.length > 0 && (
                                                            <div className="packItemOptionBlock">
                                                                <div className="packItemOptionLabel">Color</div>
                                                                <div className="packItemOptionList packItemSizeContainer">
                                                                    {colors.map((color, colorIndex) => {
                                                                        const label = getColorLabel(color);
                                                                        if (!label) return null;
                                                                        const isSelected =
                                                                            String(selection?.color || "").toLowerCase() ===
                                                                            label.toLowerCase();
                                                                        return (
                                                                            <button
                                                                                type="button"
                                                                                key={`${productKey}-color-${label}-${colorIndex}`}
                                                                                className={`packItemSingleSize text-capitalize ${isSelected ? " is-selected" : ""}`}
                                                                                onClick={() => applyColorSelection(entry, productKey, color)}
                                                                            >
                                                                                {label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {sizes.length === 0 && colors.length === 0 && (
                                                            <div className="packItemDetailSummary">
                                                                No size or color options for this product.
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="packItemFooterLike">
                                <div className="packItemOptionLabel">Quantity</div>
                                <div className="quantitySection">
                                    <div
                                        className={`minus action ${count > 1 ? "active" : "disabled"}`}
                                        onClick={decrement}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Decrease quantity"
                                        aria-disabled={count <= 1}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                decrement();
                                            }
                                        }}
                                        style={{ cursor: count > 1 ? "pointer" : "not-allowed" }}
                                    >
                                        <Minus size={40} />
                                    </div>
                                    <div className="value">{count}</div>
                                    <div
                                        className="plus action active"
                                        onClick={increment}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Increase quantity"
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                increment();
                                            }
                                        }}
                                    >
                                        <Plus size={40} />
                                    </div>
                                </div>

                                <div className="packItemOptionLabel">Price</div>
                                <div className="packItemFooterPrice priceTxt">{formatPrice(totalPrice)}</div>

                                <div className="packItemFooterActions">
                                    <button type="button" className="packItemCancelBtn btnCancel" onClick={onHide}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="packItemAddBtn btnAddtoCart"
                                        onClick={handleAddToCart}
                                        disabled={count <= 0}
                                    >
                                        <span className="me-1">
                                            <AddCart />
                                        </span>
                                        Add to Cart
                                        {/* <span className="packItemAddPrice">{formatPrice(totalPrice)}</span> */}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {successModal && (
                <AddtoCart show={successModal} onHide={() => setSuccessModal(false)} />
            )}
        </>
    );
};

export default PackItemModal;
